/**
 * Character references (CommonMark 2.5): `&name;`, `&#123;` and `&#x7b;`. The stored form never keeps one
 * (decision 66 escapes every `&`), but imported Markdown has them, and they are what the writer sees.
 */
import { characterEntities } from 'character-entities';

/** What Lezer marks as an `Entity` node, read from just after the `&` (its 30-character window included). */
export const ENTITY_SHAPE = /^(?:#\d+|#x[a-f\d]+|\w+);/iu;
/** How far past the `&` Lezer looks for the `;`. */
export const ENTITY_WINDOW = 30;

const REPLACEMENT = '\uFFFD';
/** Own entries only, so a name like `constructor` is no reference. */
const NAMED = new Map(Object.entries(characterEntities));

/** The character a numeric reference names; zero, surrogates and values past Unicode become U+FFFD. */
function fromCodePoint(code: number): string {
  const valid = code > 0 && code <= 0x10ffff && (code < 0xd800 || code > 0xdfff);
  return valid ? String.fromCodePoint(code) : REPLACEMENT;
}

/**
 * The text `reference` (a whole `&…;`, as {@link ENTITY_SHAPE} matches it) stands for, or null when it isn't a
 * character reference and so reads as the literal characters. Named references come from the HTML5 list; a
 * name that is only an object property (`constructor`) is not one. Decimal references take one to seven
 * digits, hexadecimal one to six.
 */
export function decodeEntity(reference: string): string | null {
  const body = reference.slice(1, -1);
  const hexadecimal = body.startsWith('#x') || body.startsWith('#X');
  if (hexadecimal) return body.length > 8 ? null : fromCodePoint(Number.parseInt(body.slice(2), 16));
  if (body.startsWith('#')) return body.length > 8 ? null : fromCodePoint(Number.parseInt(body.slice(1), 10));
  return NAMED.get(body) ?? null;
}
